<script setup lang="ts">
import { Minus, Plus } from 'lucide-vue-next';

const prefix = 'components.search.passengersTypesRow';

const properties = defineProps<{
  type: 'adults' | 'children' | 'infants';
  minimum: number;
  maximum: number;
}>();
const count = defineModel<number>({ default: 0 });

const labelId = useId();

// Spoken by the hidden live region: a button press, or a change this row did not make.
const announcedCount = ref<number>();

const counter = useTemplateRef('counter');
const { focused: counterFocused } = useFocus(counter);

// Arrow keys on the focused counter are already announced by the spinbutton itself, so they clear the message:
// the next button press is announced even when it repeats it. Any other change (the store capping infants at the
// adult count) is announced here, or a screen reader user would never hear it.
watch(count, (value) => {
  if (announcedCount.value !== value) {
    announcedCount.value = counterFocused.value ? undefined : value;
  }
});

const step = (delta: -1 | 1) => {
  const next = count.value + delta;
  if (next < properties.minimum || next > properties.maximum) {
    return;
  }
  count.value = next;
  announcedCount.value = next;
};
</script>

<template>
  <UiField
    orientation="horizontal"
    class="justify-between md:px-4 md:py-2"
  >
    <UiFieldContent>
      <UiFieldLabel
        :id="labelId"
        class="text-base font-medium"
      >
        {{ $t(`${prefix}.${properties.type}`) }}
      </UiFieldLabel>
      <UiFieldDescription>
        {{ $t(`${prefix}.${properties.type}Placeholder`) }}
      </UiFieldDescription>
    </UiFieldContent>

    <UiNumberField
      v-model="count"
      :min="properties.minimum"
      :max="properties.maximum"
      class="flex items-center gap-2"
    >
      <!-- Not UiNumberFieldDecrement/Increment: reka steps on pointerdown (press-and-hold), so the click a screen reader sends does nothing.
           aria-disabled, not disabled: a button disabled while focused drops focus to the page. -->
      <UiButton
        variant="secondary"
        color="brand-primary"
        shape="full"
        :aria-label="`${$t(`${prefix}.decrementLabel`)} ${$t(`${prefix}.${properties.type}`)}`"
        :aria-disabled="count <= properties.minimum"
        class="size-8 p-0 shadow-none hover:bg-brand-primary-200 aria-disabled:cursor-not-allowed aria-disabled:opacity-20 [&_svg]:size-4"
        @click="step(-1)"
      >
        <Minus />
      </UiButton>
      <UiNumberFieldInput
        ref="counter"
        :aria-labelledby="labelId"
        :aria-valuetext="String(count)"
        :aria-roledescription="$t(`${prefix}.counter`)"
        class="h-auto w-4 cursor-default border-none py-0 shadow-none selection:bg-transparent"
        readonly
      />
      <UiButton
        variant="secondary"
        color="brand-primary"
        shape="full"
        :aria-label="`${$t(`${prefix}.incrementLabel`)} ${$t(`${prefix}.${properties.type}`)}`"
        :aria-disabled="count >= properties.maximum"
        class="size-8 p-0 shadow-none hover:bg-brand-primary-200 aria-disabled:cursor-not-allowed aria-disabled:opacity-20 [&_svg]:size-4"
        @click="step(1)"
      >
        <Plus />
      </UiButton>
    </UiNumberField>
    <span
      class="sr-only"
      aria-live="polite"
    >
      <template v-if="announcedCount !== undefined">
        {{ $t(`${prefix}.${properties.type}Count`, announcedCount) }}
      </template>
    </span>
  </UiField>
</template>
